/**
 * Dependencies
 */
import Vue from 'vue'
import { getState } from './state'

/**
 * Set the pending state
 *
 * @param  {Object} state
 * @return {Void}
 */
const GET_PROJECT = (state) => {
    state.pending = true
}

/**
 * Set the retrieved project data
 *
 * @param  {Object} state
 * @param  {Object} data
 * @return {Void}
 */
const GET_PROJECT_SUCCESS = (state, payload) => {
    state.data = payload.data
    state.meta = payload.meta
}

/**
 * Handle the failed project action
 *
 * @param  {Object} state
 * @return {Void}
 */
const GET_PROJECT_FAILED = (state) => {
    state.data = {}
    state.pending = false
}

/**
 * Reset the project context
 *
 * @param  {Object} state
 * @return {Void}
 */
const RESET_PROJECT = (state) => {
    state.data = {}
    state.pending = false
    state.copy_name = ''
    state.copy_pending = false
    state.copy_success = false
    state.copy_id = null
    state.copy_message = null
    state.copy_success = false
}

/**
 * Reset the copy state
 *
 * @param  {[type]} state
 * @return {[type]}
 */
const RESET_COPY_STATE = (state) => {
    state.copy_id = null
    state.copy_saving = false
    state.copy_name = ''
    state.copy_pending = false
    state.copy_success = false
    state.copy_message = null
}

/**
 * Set the downlod pending state
 *
 * @param  {Object} state
 * @return {Void}
 */
const DOWNLOAD_KEY_GET = (state) => {
    state.excel_pending = true
}

/**
 * Set the pending state when a key is successfully downloaded
 *
 * @param  {Object} state
 * @return {Void}
 */
const DOWNLOAD_KEY_SUCCESS = (state) => {
    state.excel_pending = false
}

/**
 * Set the pending state whent the key fails to download
 *
 * @param  {Object} state
 * @return {Void}
 */
const DOWNLOAD_KEY_FAILED = (state) => {
    state.excel_pending = false
}

/**
 * Reset the Excel download context
 *
 * @param  {Object} state
 * @return {Void}
 */
const RESET_EXCEL_CONTEXT = (state) => {
    state.excel_pending = false
    state.excel_spreadsheet_url = null
    state.excel_spreadsheet_downloaded = false
}

/**
 * Set the download context state on successful download
 *
 * @param  {Object} state
 * @return {Void}
 */
const DOWNLOAD_EXCEL_SUCCESS = (state, payload) => {
    state.excel_spreadsheet_url = `${process.env.API_URL}/projects${payload.url}`
}

/**
 * Set the action state based on a successful file download
 *
 * @param  {Object} state
 * @return {Void}
 */
const EXCEL_FILE_DOWNLOADED = (state) => {
    state.excel_spreadsheet_downloaded = true
}

/**
 * Reset the state
 *
 * @param  {Object} state
 * @return {Void}
 */
const RESET_STATE = (state) => {
    let originalState = getState()
    for (let prop in originalState) {
        Vue.set(state, prop, originalState[prop])
    }
}

/**
 * Set the pending state retrieving a copied project name
 *
 * @param  {Object} state
 * @return {Void}
 */
const GET_COPY_NAME = (state) => {
    state.copy_pending = true
}

/**
 * Set the pending state retrieving a copied project name
 *
 * @param  {Object} state
 * @return {Void}
 */
const GET_COPY_NAME_SUCCESS = (state, payload) => {
    state.copy_name = payload
    state.copy_success = true
    state.copy_pending = false
}

/**
 * Set the pending state retrieving a copied project name
 *
 * @param  {Object} state
 * @return {Void}
 */
const GET_COPY_NAME_FAILURE = (state) => {
    state.copy_name = ''
    state.copy_success = false
    state.copy_pending = false
    state.copy_saving = false
    state.copy_id = null
}

/**
 * Reset copy name state
 *
 * @param  {Object} state
 * @return {Void}
 */
const RESET_COPY_NAME = (state) => {
    state.copy_name = ''
    state.copy_success = false
    state.copy_pending = false
    state.copy_saving = false
    state.copy_id = null
}

/**
 * Update the copied project name state
 *
 * @param  {Object} state
 * @param  {String} payload
 * @return {Void}
 */
const UPDATE_COPY_NAME = (state, payload) => {
    state.copy_name = payload
}

const SAVING_COPY = (state) => {
    state.copy_saving = true
}

const SAVING_COPY_SUCCESS = (state, payload) => {
    state.copy_message = payload.data.data.message
    state.copy_saving = false
    state.copy_id = payload.data.data.id
    state.copy_success = true
}

const SAVING_COPY_FAILURE = (state) => {
    state.copy_saving = false
    state.copy_id = null
}

/**
 * Set the project user management context
 *
 * @param  {Object} state
 * @param  {Object} payload
 * @return {Void}
 */
const USER_MANAGEMENT_GET_SUCCESS = (state, payload) => {
    state.management = payload
}

/**
 * Update the privilege leve for a user in the state
 *
 * @param  {Object} state
 * @param  {Object} payload
 * @return {Void}
 */
const PRIVILEGE_UPDATED_SUCCESS = (state, payload) => {
    let found = state.management.privileges.find(
        privilege => privilege.id === payload.privilegeId
    )

    if (found) found.level = Number(payload.level)
}

/**
 * Revise the privilege state
 *
 * @param  {Object} state
 * @param  {Number} privilegeId
 * @return {Void}
 */
const PRIVILEGE_REVOKED_SUCCESS = (state, privilegeId) => {
    state.management.privileges = state.management.privileges.filter(
        privilege => privilege.id !== Number(privilegeId)
    )
}

/**
 * Augment the state to include new privilege object
 *
 * @param  {Object} state
 * @param  {Object} payload
 * @return {Void}
 */
const NEW_PRIVILEGE_SUCCESS = (state, payload) => {}

/**
 * Remove an invite from the state
 *
 * @param  {Object} state
 * @param  {Number} inviteId
 * @return {Void}
 */
const INVITE_REMOVED_SUCCESS = (state, inviteId) => {
    state.management.invites = state.management.invites.filter(
        invite => invite.id !== inviteId
    )
}

/**
 * Change the invite state
 *
 * @param  {Object} state
 * @param  {Object} payload
 * @return {Void}
 */
const UPDATE_INVITE_LEVEL = (state, payload) => {
    let invite = state.management.invites.find(item => payload.invitationId === item.id)

    if (invite) invite.privilege_level = payload.level
}

/**
 * Set the history request data on the state
 *
 * @param  {Object} state
 * @param  {Object} payload
 * @return {Void}
 */
const GET_HISTORY_SUCCESS = (state, payload) => {
    state.versions.data = payload.data
    state.versions.meta = payload.meta
}

/**
 * Get the current version of a project
 *
 * @param  {Object} state
 * @param  {Object} payload
 * @return {Void}
 */
const GET_CURRENT_VERSION = (state, payload) => {
    state.versions.current_version = payload
}

/**
 * Update the project name
 *
 * @param  {Object} state
 * @param  {String} name
 * @return {Void}
 */
const UPDATE_PROJECT_NAME = (state, name) => {
    state.data.name = name
}

export default {
    RESET_STATE,
    GET_PROJECT,
    GET_COPY_NAME,
    RESET_PROJECT,
    RESET_COPY_NAME,
    UPDATE_COPY_NAME,
    DOWNLOAD_KEY_GET,
    GET_PROJECT_FAILED,
    RESET_EXCEL_CONTEXT,
    DOWNLOAD_KEY_FAILED,
    GET_PROJECT_SUCCESS,
    RESET_COPY_STATE,
    GET_HISTORY_SUCCESS,
    DOWNLOAD_KEY_SUCCESS,
    GET_COPY_NAME_FAILURE,
    GET_COPY_NAME_SUCCESS,
    EXCEL_FILE_DOWNLOADED,
    DOWNLOAD_EXCEL_SUCCESS,
    SAVING_COPY,
    GET_CURRENT_VERSION,
    SAVING_COPY_SUCCESS,
    SAVING_COPY_FAILURE,
    USER_MANAGEMENT_GET_SUCCESS,
    PRIVILEGE_UPDATED_SUCCESS,
    PRIVILEGE_REVOKED_SUCCESS,
    NEW_PRIVILEGE_SUCCESS,
    INVITE_REMOVED_SUCCESS,
    UPDATE_INVITE_LEVEL,
    UPDATE_PROJECT_NAME
}



// WEBPACK FOOTER //
// ./src/state/modules/projects/single/mutations.js